import type { DailyContext, DailyEditionEnvelope, DailyIssueItem, ItemListEnvelope, ReaderItem } from "./types";

const MAX_ITEM_SUMMARY_IDS = 32;
const MAX_DAILY_ITEM_IDS = 256;

export function dailyContext(date = new Date()): DailyContext {
  const issueDate = [date.getFullYear(), date.getMonth() + 1, date.getDate()]
    .map((part, index) => index === 0 ? String(part) : String(part).padStart(2, "0"))
    .join("-");
  const hour = date.getHours();
  const bucket = hour >= 5 && hour <= 7
    ? "early_morning"
    : hour >= 8 && hour <= 11
      ? "morning"
      : hour >= 12 && hour <= 16
        ? "afternoon"
        : hour >= 17 && hour <= 21
          ? "evening"
          : "night";
  return { issueDate, bucket };
}

export function selectedDailyItemIds(daily: DailyEditionEnvelope): string[] {
  const curated = new Map<string, { item: DailyIssueItem; position: number }>();
  let position = 0;
  const add = (item: DailyIssueItem) => {
    const id = item.item_id;
    if (!id) return;
    const existing = curated.get(id);
    if (!existing) {
      if (curated.size >= MAX_DAILY_ITEM_IDS) throw new RangeError("daily_item_limit_exceeded");
      curated.set(id, { item, position });
    } else if ((item.rank ?? Number.POSITIVE_INFINITY) < (existing.item.rank ?? Number.POSITIVE_INFINITY)) {
      curated.set(id, { item, position: existing.position });
    }
    position += 1;
  };

  if (daily.issue) {
    for (const group of daily.issue.sections || []) {
      for (const item of group.items || []) add(item);
      for (const item_id of group.item_ids || []) add({ item_id });
    }
    for (const item of daily.issue.items || []) add(item);
  } else {
    for (const entry of daily.entries) if (entry.selected !== false) add(entry);
  }

  return [...curated.values()]
    .sort((left, right) => (left.item.rank ?? Number.POSITIVE_INFINITY) - (right.item.rank ?? Number.POSITIVE_INFINITY) || left.position - right.position)
    .map(({ item }) => item.item_id);
}

export async function loadSelectedDailyItems(
  ids: string[],
  fetchSummaries: (chunk: string[]) => Promise<ItemListEnvelope>,
): Promise<ReaderItem[]> {
  if (!ids.length) return [];
  if (ids.length > MAX_DAILY_ITEM_IDS) throw new RangeError("daily_item_limit_exceeded");
  if (ids.some((id) => !id) || new Set(ids).size !== ids.length) {
    throw new TypeError("invalid_daily_item_ids");
  }
  const items: ReaderItem[] = [];
  for (let index = 0; index < ids.length; index += MAX_ITEM_SUMMARY_IDS) {
    const chunk = ids.slice(index, index + MAX_ITEM_SUMMARY_IDS);
    const envelope = await fetchSummaries(chunk);
    const allowed = new Set(chunk);
    const responseIds = envelope.items.map((item) => item.id);
    if (envelope.has_more
      || responseIds.length !== chunk.length
      || responseIds.some((id) => !id || !allowed.has(id))
      || new Set(responseIds).size !== responseIds.length
      || chunk.some((id) => !responseIds.includes(id))) {
      throw new TypeError("invalid_daily_summary_response");
    }
    items.push(...envelope.items);
  }
  const byId = new Map(items.map((item) => [item.id, item]));
  return ids.map((id) => byId.get(id) as ReaderItem);
}
